import { formResponsesQuery } from "../http/form-responses-query";
import type {
  AutomationHubView,
  FlowRunCountsView,
  FlowRunDetailView,
  FlowRunsPageView,
  FlowRunsQueryInput,
  HubRunView,
} from "../automation-hub-types";
import type { WebhookCallView, WebhookStatsView } from "../webhook-stats-types";
import type {
  ControlResponsesView,
  FlowAnswersView,
  EmergencyChannelView,
  FlowControlView,
  MetaHealthView,
} from "../flow-control-types";
import type { SystemReplyView } from "../automation-types";
import type {
  VoucherPool,
  AutomationPauseState,
  FlowDraft,
  FlowLintIssue,
  FormDraftView,
  FormResponsePageView,
  FormExportFileView,
  FormDataSourceView,
  FormSettingsResponse,
  SmartFormPreviewView,
  FormMetaSyncResultView,
  FormSummaryView,
  MetaFormCategory,
  FormTemplateView,
  FormVersionView,
  FlowExportFile,
  FlowSimResult,
  FlowStressRun,
  FlowErrorList,
  FlowErrorDetail,
  MenuHealthView,
  MenuHealthInstallResult,
  FlowTester,
  FlowEditor,
  FlowVersion,
  FlowPerformanceAlert,
  FlowVersionCounters,
  FlowVersionFunnel,
  FlowMenuFunnel,
  FlowMenuSuggestion,
  FlowVersionDiff,
  FlowNodeCatalogEntry,
  AiFlowDraft,
  AiFlowPatch,
  FlowSummary,
} from "../types";
import type {
  ContactFlowAnswer,
  ContactSequenceEnrollment,
  SequenceStatsView,
  SequenceSummary,
} from "../sequence-types";
import type { AutomationSettingView } from "../automation-admin-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

export type FlowsApiMethods = Pick<
  ApiClient,
  | "listSequences"
  | "createSequence"
  | "getSequence"
  | "updateSequence"
  | "getSequenceStats"
  | "enrollSequenceContacts"
  | "listContactSequenceEnrollments"
  | "setContactSequence"
  | "listContactFlowAnswers"
  | "getFlowNodeCatalog"
  | "listFlows"
  | "cloneFlow"
  | "createFlow"
  | "aiDraftFlow"
  | "flowFromConversation"
  | "aiPatchFlow"
  | "updateFlow"
  | "listVoucherPools"
  | "createVoucherPool"
  | "uploadVoucherCodes"
  | "listFlowFolders"
  | "createFlowFolder"
  | "renameFlowFolder"
  | "deleteFlowFolder"
  | "deleteFlow"
  | "listForms"
  | "listFormTemplates"
  | "createForm"
  | "createFormFromTemplate"
  | "deleteForm"
  | "publishForm"
  | "syncFormsFromMeta"
  | "deprecateForm"
  | "setFormArchived"
  | "setFormCategories"
  | "previewForm"
  | "listFormVersions"
  | "rollbackForm"
  | "listFormResponses"
  | "cloneForm"
  | "exportFormJson"
  | "importFormJson"
  | "getFormSettings"
  | "getFormDataSource"
  | "setFormDataSource"
  | "getSmartFormPreview"
  | "updateFormSettings"
  | "testFormDestination"
  | "getFormMapping"
  | "setFormMapping"
  | "getFormDraft"
  | "saveFormDraft"
  | "getFlowDraft"
  | "lintFlow"
  | "saveFlowDraft"
  | "simulateFlow"
  | "startFlowStress"
  | "getFlowStress"
  | "stopFlowStress"
  | "listFlowErrors"
  | "getFlowErrorDetail"
  | "resolveFlowError"
  | "exportFlow"
  | "importFlow"
  | "listFlowTesters"
  | "addFlowTester"
  | "confirmFlowTester"
  | "removeFlowTester"
  | "testSendFlow"
  | "getMenuHealth"
  | "installStarterMenu"
  | "publishFlow"
  | "unpublishFlow"
  | "rollbackFlow"
  | "listFlowVersions"
  | "getFlowCounters"
  | "getFlowFunnel"
  | "getFlowPerformanceAlerts"
  | "getFlowMenuFunnel"
  | "listFlowMenuSuggestions"
  | "resolveFlowMenuSuggestion"
  | "diffFlowVersions"
  | "sendFlowPresence"
  | "listAutomationSettings"
  | "saveAutomationSetting"
  | "getAutomationPause"
  | "pauseAutomation"
  | "resumeAutomation"
  | "pauseFlow"
  | "resumeFlow"
  | "pauseAi"
  | "resumeAi"
  | "pauseChannelAutomation"
  | "resumeChannelAutomation"
  | "getAutomationHub"
  | "listWebhookStats"
  | "listWebhookCalls"
  | "replayWebhookCall"
  | "getFlowControl"
  | "toggleControlFlow"
  | "duplicateControlFlow"
  | "archiveControlItem"
  | "bulkControlFlows"
  | "assignControlTrigger"
  | "unassignControlTrigger"
  | "setControlFlowChannel"
  | "getControlEmergency"
  | "pauseControlChannel"
  | "resumeControlChannel"
  | "getControlMetaHealth"
  | "listControlResponses"
  | "controlResponsesExportUrl"
  | "listFlowAnswers"
  | "flowAnswersExportUrl"
  | "listAutomationRuns"
  | "listFlowRuns"
  | "getFlowRunCounts"
  | "getFlowRun"
  | "stopFlowRun"
  | "setAutomationEnabled"
  | "saveSystemReply"
>;

function answersQuery(range?: { from?: string; to?: string }): string {
  const q = new URLSearchParams();
  if (range?.from) q.set("from", range.from);
  if (range?.to) q.set("to", range.to);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Query string of the runs endpoints; empty values are left out. */
function runsQueryString(query: FlowRunsQueryInput): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === false || value === "") continue;
    params.set(key, value === true ? "1" : String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}

export function createFlowsApi({ request, ws }: HttpTransport): FlowsApiMethods {
  const flowRunsPath = (id: string, flowId: string): string =>
    `${ws(id)}/flows/${encodeURIComponent(flowId)}/runs`;
  return {
    async listSequences(id) {
      return (await request<{ items: SequenceSummary[] }>("GET", ws(id) + "/sequences")).items;
    },
    createSequence: (id, input) => request<SequenceSummary>("POST", ws(id) + "/sequences", input),
    getSequence: (id, seqId) =>
      request<SequenceSummary>("GET", ws(id) + "/sequences/" + encodeURIComponent(seqId)),
    updateSequence: (id, seqId, input) =>
      request<SequenceSummary>("PATCH", ws(id) + "/sequences/" + encodeURIComponent(seqId), input),
    getSequenceStats: (id, seqId) =>
      request<SequenceStatsView>(
        "GET",
        ws(id) + "/sequences/" + encodeURIComponent(seqId) + "/stats",
      ),
    enrollSequenceContacts: (id, seqId, input) =>
      request<{ enrolled: number; skipped: number }>(
        "POST",
        ws(id) + "/sequences/" + encodeURIComponent(seqId) + "/enroll",
        input,
      ),
    async listContactFlowAnswers(id, contactId) {
      return (
        await request<{ items: ContactFlowAnswer[] }>(
          "GET",
          ws(id) + "/contacts/" + encodeURIComponent(contactId) + "/flow-answers",
        )
      ).items;
    },
    async setContactSequence(id, sequenceId, contactId, subscribed) {
      await request(
        "POST",
        ws(id) +
          "/sequences/" +
          encodeURIComponent(sequenceId) +
          (subscribed ? "/enroll" : "/unenroll"),
        subscribed ? { contactId, source: "api" } : { contactId },
      );
    },
    async listContactSequenceEnrollments(id, contactId) {
      return (
        await request<{ items: ContactSequenceEnrollment[] }>(
          "GET",
          ws(id) + "/contacts/" + encodeURIComponent(contactId) + "/sequence-enrollments",
        )
      ).items;
    },
    async getFlowNodeCatalog(id) {
      return (
        await request<{ nodes: FlowNodeCatalogEntry[] }>("GET", `${ws(id)}/flows/node-catalog`)
      ).nodes;
    },
    async listFlows(id, query = {}) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v) params.set(k, v);
      const qs = params.size > 0 ? "?" + params.toString() : "";
      return (await request<{ items: FlowSummary[] }>("GET", ws(id) + "/flows" + qs)).items;
    },
    cloneFlow: (id, flowId, input = {}) =>
      request<FlowSummary>(
        "POST",
        ws(id) + "/flows/" + encodeURIComponent(flowId) + "/clone",
        input,
      ),
    createFlow: (id, input) => request<FlowSummary>("POST", ws(id) + "/flows", input),
    aiDraftFlow: (id, prompt) =>
      request<AiFlowDraft>("POST", ws(id) + "/flows/ai-draft", { prompt }),
    flowFromConversation: (id, conversationId) =>
      request<AiFlowDraft>("POST", ws(id) + "/flows/from-conversation", { conversationId }),
    aiPatchFlow: (id, flowId, input) =>
      request<AiFlowPatch>(
        "POST",
        ws(id) + "/flows/" + encodeURIComponent(flowId) + "/generate-patch",
        input,
      ),
    updateFlow: (id, flowId, input) =>
      request<FlowSummary>("PATCH", ws(id) + "/flows/" + encodeURIComponent(flowId), input),
    listVoucherPools: async (id) =>
      (await request<{ items: VoucherPool[] }>("GET", `${ws(id)}/voucher-pools`)).items,
    createVoucherPool: (id, name) =>
      request<VoucherPool>("POST", `${ws(id)}/voucher-pools`, { name }),
    uploadVoucherCodes: (id, poolId, csv) =>
      request<{ added: number; skipped: number }>(
        "POST",
        `${ws(id)}/voucher-pools/${encodeURIComponent(poolId)}/codes`,
        { csv },
      ),
    listFlowFolders: async (id) =>
      (
        await request<{ items: { name: string; flowCount: number }[] }>(
          "GET",
          ws(id) + "/flow-folders",
        )
      ).items,
    createFlowFolder: (id, name) => request("POST", ws(id) + "/flow-folders", { name }),
    renameFlowFolder: (id, from, to) =>
      request("PATCH", ws(id) + "/flow-folders/" + encodeURIComponent(from), { name: to }),
    deleteFlowFolder: (id, name) =>
      request("DELETE", ws(id) + "/flow-folders/" + encodeURIComponent(name)),
    deleteFlow: (id, flowId) =>
      request<void>("DELETE", ws(id) + "/flows/" + encodeURIComponent(flowId)),
    listForms: async (id) =>
      (await request<{ items: FormSummaryView[] }>("GET", ws(id) + "/forms")).items,
    listFormTemplates: async (id) =>
      (await request<{ items: FormTemplateView[] }>("GET", ws(id) + "/forms/templates")).items,
    createForm: (id, input) => request<FormSummaryView>("POST", ws(id) + "/forms", input),
    createFormFromTemplate: (id, input) =>
      request<FormSummaryView>("POST", ws(id) + "/forms/from-template", input),
    deleteForm: (id, formId) =>
      request<void>("DELETE", ws(id) + "/forms/" + encodeURIComponent(formId)),
    publishForm: (id, formId) =>
      request<FormVersionView>(
        "POST",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/publish",
      ),
    syncFormsFromMeta: (id, channelId) =>
      request<FormMetaSyncResultView>("POST", ws(id) + "/forms/sync-meta", { channelId }),
    deprecateForm: (id, formId) =>
      request<{ status: string }>(
        "POST",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/deprecate",
      ),
    setFormArchived: (id, formId, archived) =>
      request<FormSummaryView>(
        "POST",
        ws(id) + "/forms/" + encodeURIComponent(formId) + (archived ? "/archive" : "/unarchive"),
      ),
    setFormCategories: (id, formId, categories) =>
      request<{ categories: MetaFormCategory[] }>(
        "PUT",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/categories",
        { categories },
      ),
    previewForm: (id, formId) =>
      request<{ previewUrl: string }>(
        "POST",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/preview",
      ),
    listFormVersions: async (id, formId) =>
      (
        await request<{ items: FormVersionView[] }>(
          "GET",
          ws(id) + "/forms/" + encodeURIComponent(formId) + "/versions",
        )
      ).items,
    rollbackForm: (id, formId, versionId) =>
      request<FormVersionView>(
        "POST",
        ws(id) +
          "/forms/" +
          encodeURIComponent(formId) +
          "/rollback/" +
          encodeURIComponent(versionId),
      ),
    listFormResponses: (id, formId, cursor, range) =>
      request<FormResponsePageView>(
        "GET",
        ws(id) +
          "/forms/" +
          encodeURIComponent(formId) +
          "/responses" +
          formResponsesQuery({ ...range, ...(cursor ? { cursor } : {}) }),
      ),
    cloneForm: (id, formId) =>
      request<FormSummaryView>("POST", ws(id) + "/forms/" + encodeURIComponent(formId) + "/clone"),
    exportFormJson: (id, formId) =>
      request<FormExportFileView>(
        "GET",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/export",
      ),
    importFormJson: (id, file) =>
      request<FormSummaryView>("POST", ws(id) + "/forms/import", { file }),
    getSmartFormPreview: (id, formId) =>
      request<SmartFormPreviewView>(
        "GET",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/smart-preview",
      ),
    getFormDataSource: (id, formId) =>
      request<FormDataSourceView>(
        "GET",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/data-source",
      ),
    setFormDataSource: (id, formId, binding) =>
      request<FormDataSourceView>(
        "PUT",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/data-source",
        { binding },
      ),
    getFormSettings: (id, formId) =>
      request<FormSettingsResponse>(
        "GET",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/settings",
      ),
    updateFormSettings: (id, formId, input) =>
      request<FormSettingsResponse>(
        "PUT",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/settings",
        input,
      ),
    testFormDestination: (id, formId, kind) =>
      request<{ ok: boolean; status?: number; error?: string }>(
        "POST",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/settings/test-" + kind,
        {},
      ),
    getFormMapping: async (id, formId) =>
      (
        await request<{ mapping: Record<string, string> }>(
          "GET",
          ws(id) + "/forms/" + encodeURIComponent(formId) + "/mapping",
        )
      ).mapping,
    setFormMapping: async (id, formId, mapping) =>
      (
        await request<{ mapping: Record<string, string> }>(
          "PUT",
          ws(id) + "/forms/" + encodeURIComponent(formId) + "/mapping",
          { mapping },
        )
      ).mapping,
    getFormDraft: (id, formId) =>
      request<FormDraftView>("GET", ws(id) + "/forms/" + encodeURIComponent(formId) + "/draft"),
    saveFormDraft: (id, formId, input) =>
      request<FormDraftView>(
        "PATCH",
        ws(id) + "/forms/" + encodeURIComponent(formId) + "/draft",
        input,
      ),
    async lintFlow(id, flowId) {
      return (
        await request<{ issues: FlowLintIssue[] }>(
          "GET",
          ws(id) + "/flows/" + encodeURIComponent(flowId) + "/lint",
        )
      ).issues;
    },
    getFlowDraft: (id, flowId) =>
      request<FlowDraft>("GET", ws(id) + "/flows/" + encodeURIComponent(flowId) + "/draft"),
    saveFlowDraft: (id, flowId, input) =>
      request<FlowDraft>(
        "PATCH",
        ws(id) + "/flows/" + encodeURIComponent(flowId) + "/draft",
        input,
      ),
    simulateFlow: (id, flowId, input) =>
      request<FlowSimResult>(
        "POST",
        ws(id) + "/flows/" + encodeURIComponent(flowId) + "/simulate",
        input,
      ),
    startFlowStress: async (id, flowId, runs) =>
      (
        await request<{ run: FlowStressRun }>(
          "POST",
          ws(id) + "/flows/" + encodeURIComponent(flowId) + "/stress-test",
          { runs },
        )
      ).run,
    getFlowStress: async (id, flowId) =>
      (
        await request<{ run: FlowStressRun | null }>(
          "GET",
          ws(id) + "/flows/" + encodeURIComponent(flowId) + "/stress-test",
        )
      ).run,
    stopFlowStress: async (id, flowId) => {
      await request<void>(
        "POST",
        ws(id) + "/flows/" + encodeURIComponent(flowId) + "/stress-test/stop",
        {},
      );
    },
    listFlowErrors: (id, filter) => {
      const query = new URLSearchParams();
      if (filter?.status) query.set("status", filter.status);
      if (filter?.flowId) query.set("flowId", filter.flowId);
      if (filter?.channelId) query.set("channelId", filter.channelId);
      const qs = query.toString();
      return request<FlowErrorList>("GET", ws(id) + "/flow-errors" + (qs ? "?" + qs : ""));
    },
    getFlowErrorDetail: (id, key) =>
      request<FlowErrorDetail>(
        "GET",
        ws(id) + "/flow-errors/detail?key=" + encodeURIComponent(key),
      ),
    resolveFlowError: async (id, key) => {
      await request<void>("POST", ws(id) + "/flow-errors/resolve", { key });
    },
    exportFlow: (id, flowId) =>
      request<FlowExportFile>("GET", ws(id) + "/flows/" + encodeURIComponent(flowId) + "/export"),
    importFlow: (id, input) => request<FlowSummary>("POST", ws(id) + "/flows/import", input),
    async listFlowTesters(id) {
      return (await request<{ items: FlowTester[] }>("GET", ws(id) + "/flow-testers")).items;
    },
    addFlowTester: (id, input) => request<FlowTester>("POST", ws(id) + "/flow-testers", input),
    confirmFlowTester: (id, testerId, code) =>
      request<FlowTester>(
        "POST",
        ws(id) + "/flow-testers/" + encodeURIComponent(testerId) + "/confirm",
        { code },
      ),
    async removeFlowTester(id, testerId) {
      await request<void>("DELETE", ws(id) + "/flow-testers/" + encodeURIComponent(testerId));
    },
    testSendFlow: (id, flowId, testerId) =>
      request<{ executionId: string }>(
        "POST",
        ws(id) + "/flows/" + encodeURIComponent(flowId) + "/test-send",
        { testerId },
      ),
    getMenuHealth: (id) => request<MenuHealthView>("GET", ws(id) + "/automation/menu-health"),
    installStarterMenu: (id) =>
      request<MenuHealthInstallResult>("POST", ws(id) + "/automation/menu-health/install", {}),
    publishFlow: (id, flowId, input = {}) =>
      request<FlowVersion>("POST", ws(id) + "/flows/" + encodeURIComponent(flowId) + "/publish", {
        acceptWarnings: input.acceptWarnings === true,
      }),
    unpublishFlow: (id, flowId) =>
      request<FlowSummary>("POST", ws(id) + "/flows/" + encodeURIComponent(flowId) + "/unpublish"),
    rollbackFlow: (id, flowId, versionId) =>
      request<FlowVersion>(
        "POST",
        ws(id) +
          "/flows/" +
          encodeURIComponent(flowId) +
          "/rollback/" +
          encodeURIComponent(versionId),
      ),
    async listFlowVersions(id, flowId) {
      return (
        await request<{ items: FlowVersion[] }>(
          "GET",
          ws(id) + "/flows/" + encodeURIComponent(flowId) + "/versions",
        )
      ).items;
    },
    getFlowMenuFunnel: (id, flowId, window) =>
      request<FlowMenuFunnel>(
        "GET",
        ws(id) + "/flows/" + encodeURIComponent(flowId) + "/menu-funnel?window=" + window,
      ),
    listFlowMenuSuggestions: async (id, flowId) =>
      (
        await request<{ items: FlowMenuSuggestion[] }>(
          "GET",
          ws(id) + "/flows/" + encodeURIComponent(flowId) + "/menu-suggestions",
        )
      ).items,
    resolveFlowMenuSuggestion: async (id, flowId, suggestionId, status) => {
      await request(
        "POST",
        ws(id) +
          "/flows/" +
          encodeURIComponent(flowId) +
          "/menu-suggestions/" +
          encodeURIComponent(suggestionId) +
          "/resolve",
        { status },
      );
    },
    getFlowPerformanceAlerts: async (id, flowId) =>
      (
        await request<{ items: FlowPerformanceAlert[] }>(
          "GET",
          ws(id) + "/flows/" + encodeURIComponent(flowId) + "/performance-alerts",
        )
      ).items,
    getFlowCounters: (id, flowId, versionId, window) =>
      request<FlowVersionCounters>(
        "GET",
        ws(id) +
          "/flows/" +
          encodeURIComponent(flowId) +
          "/versions/" +
          encodeURIComponent(versionId) +
          "/counters?window=" +
          window,
      ),
    getFlowFunnel: (id, flowId, versionId, window) =>
      request<FlowVersionFunnel>(
        "GET",
        ws(id) +
          "/flows/" +
          encodeURIComponent(flowId) +
          "/versions/" +
          encodeURIComponent(versionId) +
          "/funnel?window=" +
          window,
      ),
    diffFlowVersions: (id, flowId, a, b) =>
      request<FlowVersionDiff>(
        "GET",
        ws(id) +
          "/flows/" +
          encodeURIComponent(flowId) +
          "/diff?a=" +
          encodeURIComponent(a) +
          "&b=" +
          encodeURIComponent(b),
      ),
    async sendFlowPresence(id, flowId, input) {
      return (
        await request<{ editors: FlowEditor[] }>(
          "POST",
          ws(id) + "/flows/" + encodeURIComponent(flowId) + "/presence",
          input,
        )
      ).editors;
    },
    async listAutomationSettings(id) {
      return (
        await request<{ settings: AutomationSettingView[] }>("GET", `${ws(id)}/automation-settings`)
      ).settings;
    },
    async saveAutomationSetting(id, type, input) {
      await request("PUT", `${ws(id)}/automation-settings/${type}`, input);
    },
    getAutomationPause: (id) => request<AutomationPauseState>("GET", `${ws(id)}/automation/pause`),
    pauseAutomation: (id, reason) =>
      request<AutomationPauseState>("POST", `${ws(id)}/automation/pause`, reason ? { reason } : {}),
    resumeAutomation: (id) => request<AutomationPauseState>("POST", `${ws(id)}/automation/resume`),
    pauseFlow: (id, flowId, reason) =>
      request<AutomationPauseState>(
        "POST",
        `${ws(id)}/flows/${encodeURIComponent(flowId)}/pause`,
        reason ? { reason } : {},
      ),
    resumeFlow: (id, flowId) =>
      request<AutomationPauseState>("POST", `${ws(id)}/flows/${encodeURIComponent(flowId)}/resume`),
    pauseAi: (id, reason) =>
      request<AutomationPauseState>(
        "POST",
        `${ws(id)}/automation/ai/pause`,
        reason ? { reason } : {},
      ),
    resumeAi: (id) => request<AutomationPauseState>("POST", `${ws(id)}/automation/ai/resume`),
    pauseChannelAutomation: (id, channelId, reason) =>
      request<AutomationPauseState>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/automation/pause`,
        reason ? { reason } : {},
      ),
    resumeChannelAutomation: (id, channelId) =>
      request<AutomationPauseState>(
        "POST",
        `${ws(id)}/channels/${encodeURIComponent(channelId)}/automation/resume`,
      ),
    getAutomationHub: (id) => request<AutomationHubView>("GET", `${ws(id)}/automation-hub`),
    listWebhookStats: async (id) =>
      (await request<{ items: WebhookStatsView[] }>("GET", `${ws(id)}/webhook-triggers/stats`))
        .items,
    listWebhookCalls: async (id, triggerId) =>
      (
        await request<{ items: WebhookCallView[] }>(
          "GET",
          `${ws(id)}/webhook-triggers/${encodeURIComponent(triggerId)}/calls`,
        )
      ).items,
    replayWebhookCall: (id, deliveryId) =>
      request<{ status: string; deliveryId: string }>(
        "POST",
        `${ws(id)}/webhook-calls/${encodeURIComponent(deliveryId)}/replay`,
        {},
      ),
    getFlowControl(id, filters = {}) {
      const qs = new URLSearchParams(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== "") as [
          string,
          string,
        ][],
      ).toString();
      return request<FlowControlView>("GET", `${ws(id)}/flow-control${qs ? `?${qs}` : ""}`);
    },
    async toggleControlFlow(id, flowId, enabled, reason) {
      await request<unknown>(
        "POST",
        `${ws(id)}/flow-control/flows/${encodeURIComponent(flowId)}/toggle`,
        {
          enabled,
          ...(reason ? { reason } : {}),
        },
      );
    },
    duplicateControlFlow: (id, flowId) =>
      request<{ id: string; name: string }>(
        "POST",
        `${ws(id)}/flow-control/flows/${encodeURIComponent(flowId)}/duplicate`,
        {},
      ),
    bulkControlFlows: (id, action, flowIds, reason) =>
      request<{ done: string[]; failed: { id: string; code: string }[] }>(
        "POST",
        `${ws(id)}/flow-control/flows/bulk`,
        { action, flowIds, ...(reason ? { reason } : {}) },
      ),
    async archiveControlItem(id, kind, itemId, archived) {
      await request<unknown>(
        "POST",
        `${ws(id)}/flow-control/${kind === "automation" ? "flows" : "forms"}/${encodeURIComponent(itemId)}/archive`,
        { archived },
      );
    },
    assignControlTrigger: (id, flowId, input) =>
      request<{ triggerId: string }>(
        "POST",
        `${ws(id)}/flow-control/flows/${encodeURIComponent(flowId)}/triggers`,
        input,
      ),
    async unassignControlTrigger(id, triggerId) {
      await request<unknown>(
        "DELETE",
        `${ws(id)}/flow-control/triggers/${encodeURIComponent(triggerId)}`,
      );
    },
    async setControlFlowChannel(id, flowId, channelId) {
      await request<unknown>(
        "PUT",
        `${ws(id)}/flow-control/flows/${encodeURIComponent(flowId)}/channel`,
        {
          channelId,
        },
      );
    },
    async getControlEmergency(id) {
      return (
        await request<{ channels: EmergencyChannelView[] }>(
          "GET",
          `${ws(id)}/flow-control/emergency`,
        )
      ).channels;
    },
    async pauseControlChannel(id, input) {
      return (
        await request<{ channels: EmergencyChannelView[] }>(
          "POST",
          `${ws(id)}/flow-control/emergency/pause`,
          input,
        )
      ).channels;
    },
    async resumeControlChannel(id, channelId) {
      return (
        await request<{ channels: EmergencyChannelView[] }>(
          "POST",
          `${ws(id)}/flow-control/emergency/resume`,
          { channelId },
        )
      ).channels;
    },
    async getControlMetaHealth(id) {
      return (
        await request<{ items: MetaHealthView[] }>("GET", `${ws(id)}/flow-control/meta-health`)
      ).items;
    },
    listControlResponses: (id, formId) =>
      request<ControlResponsesView>(
        "GET",
        `${ws(id)}/flow-control/forms/${encodeURIComponent(formId)}/responses`,
      ),
    controlResponsesExportUrl: (id, formId) =>
      `${ws(id)}/flow-control/forms/${encodeURIComponent(formId)}/responses/export`,
    listFlowAnswers: (id, flowId, range) =>
      request<FlowAnswersView>(
        "GET",
        `${ws(id)}/flow-control/flows/${encodeURIComponent(flowId)}/answers${answersQuery(range)}`,
      ),
    flowAnswersExportUrl: (id, flowId, range) =>
      `${ws(id)}/flow-control/flows/${encodeURIComponent(flowId)}/answers/export${answersQuery(range)}`,
    async listAutomationRuns(id, triggerId) {
      return (
        await request<{ runs: HubRunView[] }>(
          "GET",
          `${ws(id)}/automation-hub/${encodeURIComponent(triggerId)}/runs`,
        )
      ).runs;
    },
    listFlowRuns: (id, flowId, query = {}) =>
      request<FlowRunsPageView>("GET", `${flowRunsPath(id, flowId)}${runsQueryString(query)}`),
    getFlowRunCounts: (id, flowId, query = {}) =>
      request<FlowRunCountsView>(
        "GET",
        `${flowRunsPath(id, flowId)}/counts${runsQueryString(query)}`,
      ),
    getFlowRun: (id, flowId, executionId) =>
      request<FlowRunDetailView>(
        "GET",
        `${flowRunsPath(id, flowId)}/${encodeURIComponent(executionId)}`,
      ),
    stopFlowRun: async (id, flowId, executionId) => {
      await request<{ stopped: true }>(
        "POST",
        `${flowRunsPath(id, flowId)}/${encodeURIComponent(executionId)}/stop`,
        {},
      );
    },
    setAutomationEnabled: (id, triggerId, enabled) =>
      request<void>("PATCH", `${ws(id)}/automation-hub/${encodeURIComponent(triggerId)}`, {
        enabled,
      }),
    saveSystemReply: (id, input) =>
      request<SystemReplyView>("PUT", `${ws(id)}/system-replies`, input),
  };
}
